// WebGL wave background for the market "looks" scroller.
// Ported from a React + ogl component (21st.dev) to vanilla JS — no React, no build step.
// Loaded as type="module" only by sections/mien-look.liquid.
import { Renderer, Program, Mesh, Triangle, Vec2 } from "https://esm.sh/ogl";

const VERT = /* glsl */ `
attribute vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
#ifdef GL_ES
precision mediump float;
#endif
uniform vec2 uResolution;
uniform float uTime;
uniform int uDarkTheme;
uniform int uMono;
#define PI 3.14159265359
vec3 lightPalette(float t) {
  return mix(vec3(0.9, 0.95, 1.0), vec3(0.2, 0.4, 0.8), 0.5 + 0.5 * sin(6.2831 * t));
}
vec3 darkPalette(float t) {
  return mix(vec3(0.05, 0.1, 0.2), vec3(0.1, 0.6, 0.9), 0.5 + 0.5 * cos(6.2831 * t));
}
vec3 monoPalette(float t) {
  return mix(vec3(0.02), vec3(0.16), 0.5 + 0.5 * sin(6.2831 * t));
}
float wave(vec2 uv, float speed, float offset) {
  return sin(uv.x * 6.0 + uTime * speed + offset) * 0.3 +
         cos(uv.y * 4.0 - uTime * speed * 0.5 + offset) * 0.2;
}
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution.xy;
  uv = uv * 2.0 - 1.0;
  uv.x *= uResolution.x / uResolution.y;
  float w1 = wave(uv, 1.2, 0.0);
  float w2 = wave(uv, 0.8, 2.0);
  float w3 = wave(uv, 1.5, 4.0);
  float pattern = (w1 + w2 + w3) * 0.5;
  vec3 col;
  if (uMono == 1) { col = monoPalette(pattern + uTime * 0.05); }
  else if (uDarkTheme == 1) { col = darkPalette(pattern + uTime * 0.05); }
  else { col = lightPalette(pattern + uTime * 0.05); }
  gl_FragColor = vec4(col, 1.0);
}
`;

function initLooksWave(container) {
  if (!container || container.dataset.waveInit) return null;
  container.dataset.waveInit = "1";
  const mode = (container.dataset.waveMode || "light").toLowerCase();

  // fail silently to the section's existing background — never a broken canvas
  let renderer, gl, canvas;
  // resolutionScale capped at 0.6 on mobile widths for battery (buffer only; CSS size stays 100%)
  const resolutionScale = window.innerWidth <= 768 ? 0.6 : 1;
  try {
    renderer = new Renderer({ dpr: Math.min(window.devicePixelRatio || 1, 2), alpha: false, antialias: false, depth: false });
    gl = renderer.gl;
    if (!gl) return null;
    canvas = gl.canvas;
  } catch (e) {
    return null;
  }

  // fixed full-viewport so the waves fill the whole screen with no cut-off.
  // No z-index: the canvas paints first (section's first child) and content paints above by
  // DOM order — adding z-index forces z-indexed content wrappers, whose stacking contexts
  // isolate the product images' mix-blend-mode from the canvas (white-box bug).
  // The .wave-canvas class lets the section CSS show it only while the looks panel is active.
  canvas.className = "wave-canvas";
  canvas.style.cssText = "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;";
  container.prepend(canvas);
  container.classList.add("has-wave-bg");

  const geometry = new Triangle(gl);
  const program = new Program(gl, {
    vertex: VERT,
    fragment: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uResolution: { value: new Vec2(1, 1) },
      uDarkTheme: { value: mode === "dark" ? 1 : 0 },
      uMono: { value: mode === "mono" ? 1 : 0 },
    },
  });
  const mesh = new Mesh(gl, { geometry, program });

  function resize() {
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    // match the source component: buffer scaled, uResolution in CSS pixels
    renderer.setSize(w * resolutionScale, h * resolutionScale);
    program.uniforms.uResolution.value.set(w, h);
    // ogl's setSize writes px style dimensions — keep the canvas filling the viewport
    canvas.style.width = "100%";
    canvas.style.height = "100%";
  }
  window.addEventListener("resize", resize);
  resize();

  let raf = 0;
  let running = false;
  const t0 = performance.now();
  function frame(now) {
    raf = requestAnimationFrame(frame);
    program.uniforms.uTime.value = (now - t0) * 0.001;
    renderer.render({ scene: mesh });
  }
  function play() {
    if (running) return;
    running = true;
    resize(); // panel size may have changed while hidden
    raf = requestAnimationFrame(frame);
  }
  function pause() {
    running = false;
    cancelAnimationFrame(raf);
  }

  let io = null;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    // one static frame, no loop
    renderer.render({ scene: mesh });
  } else {
    // pause the rAF loop whenever the canvas is offscreen or hidden (non-looks panels)
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.isIntersecting ? play() : pause(); });
    });
    io.observe(canvas);
  }

  return function destroy() {
    pause();
    if (io) io.disconnect();
    window.removeEventListener("resize", resize);
    const ext = gl.getExtension("WEBGL_lose_context");
    if (ext) ext.loseContext();
    canvas.remove();
    container.classList.remove("has-wave-bg");
    delete container.dataset.waveInit;
  };
}

document.querySelectorAll("[data-wave-bg]").forEach(function (el) {
  try { initLooksWave(el); } catch (e) { /* fail silently */ }
});
